import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Circle, Search } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { cn } from '../base/utils';
import type {
  UnifiedDropdownMode,
  UnifiedDropdownGroup,
  UnifiedDropdownOption,
  UnifiedDropdownPlacement,
  UnifiedDropdownProps,
  UnifiedDropdownValue,
} from './types';

const placementMap: Record<
  UnifiedDropdownPlacement,
  { side: 'top' | 'bottom'; align: 'start' | 'end' }
> = {
  'bottom-start': { side: 'bottom', align: 'start' },
  'bottom-end': { side: 'bottom', align: 'end' },
  'top-start': { side: 'top', align: 'start' },
  'top-end': { side: 'top', align: 'end' },
};

function flattenOptions(
  options: readonly UnifiedDropdownOption[],
  depth = 0,
): Array<UnifiedDropdownOption & { depth: number }> {
  return options.flatMap((option) => [
    { ...option, depth },
    ...(option.children ? flattenOptions(option.children, depth + 1) : []),
  ]);
}

function validateOptions(options: readonly UnifiedDropdownOption[]) {
  const values = new Set<string>();
  flattenOptions(options).forEach((option) => {
    if (!option || typeof option !== 'object') {
      throw new TypeError('UnifiedDropdown options must contain objects.');
    }
    if (typeof option.value !== 'string' || !option.value.trim()) {
      throw new TypeError('UnifiedDropdown option value must be a non-empty string.');
    }
    if (values.has(option.value)) {
      throw new TypeError(`UnifiedDropdown option value "${option.value}" must be unique.`);
    }
    values.add(option.value);
  });
}

function normalizeValues(value: UnifiedDropdownValue | undefined) {
  const values = Array.isArray(value) ? value : value === undefined ? [] : [value];
  return [...new Set(values.filter((item): item is string => typeof item === 'string'))];
}

function getOptionText(option: UnifiedDropdownOption) {
  if (option.textValue) return option.textValue;
  return typeof option.label === 'string' ? option.label : option.value;
}

function isMultiMode(mode: UnifiedDropdownMode) {
  return mode === 'multi' || mode === 'checkbox';
}

function matchesSearch(option: UnifiedDropdownOption, query: string) {
  return getOptionText(option).toLocaleLowerCase().includes(query.toLocaleLowerCase());
}

function validateValueShape(
  mode: UnifiedDropdownMode,
  value: UnifiedDropdownValue | undefined,
  name: 'value' | 'defaultValue',
) {
  if (value === undefined) return;
  if (isMultiMode(mode) ? !Array.isArray(value) : typeof value !== 'string') {
    throw new TypeError(
      `UnifiedDropdown ${name} must be ${isMultiMode(mode) ? 'a string array' : 'a string'} in ${mode} mode.`,
    );
  }
}

export const UnifiedDropdown = forwardRef<HTMLDivElement, UnifiedDropdownProps>(
  function UnifiedDropdown(
    {
      mode,
      options = [],
      groups = [],
      value,
      defaultValue,
      placeholder = 'Select…',
      trigger,
      triggerLabel,
      searchPlaceholder = 'Search…',
      emptyLabel = 'No options',
      loadingLabel = 'Loading…',
      clearLabel = 'Clear selection',
      retryLabel = 'Retry',
      loading = false,
      error,
      searchable = mode === 'searchable',
      clearable = false,
      maxSelections,
      disabled = false,
      invalid = false,
      open: controlledOpen,
      defaultOpen = false,
      placement = 'bottom-start',
      closeOnSelect = mode !== 'multi' && mode !== 'checkbox',
      portal = true,
      size = 'default',
      width = 'trigger',
      appearance,
      className,
      classNames,
      slots,
      style,
      onOpenChange,
      onValueChange,
      onSelect,
      onAction,
      onClear,
      onRetry,
      ...rootProps
    },
    ref,
  ) {
    if (!['single', 'multi', 'searchable', 'radio', 'checkbox', 'tree', 'action'].includes(mode)) {
      throw new TypeError(`UnifiedDropdown mode "${String(mode)}" is not supported.`);
    }
    const groupedOptions = useMemo(
      () => groups.reduce<UnifiedDropdownOption[]>((result, group: UnifiedDropdownGroup) => {
        const groupName = typeof group.label === 'string' ? group.label : group.value;
        result.push(...group.options.map((option) => ({ ...option, group: option.group ?? groupName })));
        return result;
      }, [...options]),
      [groups, options],
    );
    validateOptions(groupedOptions);
    if (maxSelections !== undefined && (!Number.isFinite(maxSelections) || maxSelections < 0)) {
      throw new TypeError('UnifiedDropdown maxSelections must be a non-negative finite number.');
    }
    if (mode === 'action' && (value !== undefined || defaultValue !== undefined)) {
      throw new TypeError('UnifiedDropdown action mode does not accept a value.');
    }
    if (mode !== 'action') {
      validateValueShape(mode, value, 'value');
      validateValueShape(mode, defaultValue, 'defaultValue');
    }

    const id = useId();
    const triggerId = `${id}-trigger`;
    const listId = `${id}-listbox`;
    const searchId = `${id}-search`;
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const [uncontrolledValue, setUncontrolledValue] = useState<UnifiedDropdownValue>(
      defaultValue ?? (isMultiMode(mode) ? [] : ''),
    );
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const selectedValues = normalizeValues(value ?? uncontrolledValue);
    const flattened = useMemo(() => flattenOptions(groupedOptions), [groupedOptions]);
    const visibleOptions = useMemo(
      () => (searchable ? flattened.filter((option) => matchesSearch(option, query)) : flattened),
      [flattened, query, searchable],
    );
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const selectedOptions = flattened.filter((option) => selectedSet.has(option.value));
    const selectedLabel = selectedOptions.map(getOptionText).join(', ');
    const resolvedTriggerLabel = triggerLabel ?? (selectedLabel || placeholder);
    const isPlaceholder = selectedOptions.length === 0 && !triggerLabel;
    const { side, align } = placementMap[placement];
    const isMenu = mode === 'action';
    const usesCheckedMenu = mode === 'radio' || mode === 'checkbox';
    const contentRole = isMenu || usesCheckedMenu ? 'menu' : 'listbox';

    useEffect(() => {
      if (open) setActiveIndex(0);
    }, [open, query]);

    const setOpen = (nextOpen: boolean) => {
      if (disabled) return;
      if (!isControlled) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    };

    const commitValue = (nextValue: UnifiedDropdownValue) => {
      if (value === undefined) setUncontrolledValue(nextValue);
      onValueChange?.(nextValue);
    };

    const selectOption = (option: UnifiedDropdownOption) => {
      if (disabled || option.disabled) return;
      onSelect?.(option);
      if (mode === 'action') {
        if (closeOnSelect) setOpen(false);
        if (onAction) queueMicrotask(() => onAction(option));
        return;
      }
      if (isMultiMode(mode)) {
        const nextValues = selectedSet.has(option.value)
          ? selectedValues.filter((item) => item !== option.value)
          : maxSelections !== undefined && selectedValues.length >= maxSelections
            ? selectedValues
            : [...selectedValues, option.value];
        commitValue(nextValues);
      } else {
        commitValue(option.value);
      }
      if (closeOnSelect && !isMultiMode(mode)) setOpen(false);
    };

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) setOpen(true);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((index) => Math.max(0, Math.min(visibleOptions.length - 1, index + direction)));
      }
    };

    const handleContentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((index) => {
          const next = index + direction;
          return Math.max(0, Math.min(visibleOptions.length - 1, next));
        });
      }
      if (event.key === 'Enter' && visibleOptions[activeIndex]) {
        event.preventDefault();
        selectOption(visibleOptions[activeIndex]!);
      }
    };

    useEffect(() => {
      if (!open) return;
      itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open]);

    const clearSelection = () => {
      commitValue(isMultiMode(mode) ? [] : '');
      onClear?.();
    };

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <div
          {...rootProps}
          ref={ref}
          className={cn('relative inline-flex min-w-0', className)}
          style={style}
          data-monkeys-component="unified-dropdown"
          data-mode={mode}
          data-state={open ? 'open' : 'closed'}
          data-invalid={invalid || undefined}
          data-disabled={disabled || undefined}
          data-appearance-preset={appearance?.preset}
          data-appearance-variant={appearance?.variant}
          data-appearance-density={appearance?.density}
          data-appearance-radius={appearance?.radius}
        >
          <PopoverPrimitive.Trigger asChild>
            <button
              ref={triggerRef}
              id={triggerId}
              type="button"
              disabled={disabled}
              aria-expanded={open}
              aria-haspopup={isMenu || usesCheckedMenu ? 'menu' : 'listbox'}
              aria-controls={open ? listId : undefined}
              aria-invalid={invalid || undefined}
              onKeyDown={handleTriggerKeyDown}
              className={cn(
                'flex min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-control-surface px-3 text-left text-sm text-foreground shadow-sm outline-none transition-colors',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-control-surface',
                'disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' ? 'h-8 py-1 text-xs' : 'h-10 py-2',
                invalid && 'border-destructive focus-visible:ring-destructive',
                width === 'responsive' && 'w-full sm:w-auto',
                classNames?.trigger,
              )}
            >
              {trigger ? <span className="min-w-0 shrink-0">{trigger}</span> : null}
              <span className={cn('min-w-0 flex-1 truncate', isPlaceholder && 'text-muted-foreground')}>
                {resolvedTriggerLabel}
              </span>
              <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 opacity-60" />
            </button>
          </PopoverPrimitive.Trigger>
          {(() => {
            const content = (
              <PopoverPrimitive.Content
              id={listId}
              side={side}
              align={align}
              sideOffset={6}
              collisionPadding={8}
              avoidCollisions
              onKeyDown={handleContentKeyDown}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                const target = searchable
                  ? document.getElementById(searchId)
                  : itemRefs.current[0];
                target?.focus();
              }}
              tabIndex={-1}
              className={cn(
                'z-50 max-h-[min(24rem,calc(100vh-1rem))] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-md border bg-menu-surface p-1 text-popover-foreground shadow-md outline-none',
                width === 'trigger' && 'w-[var(--radix-popover-trigger-width)] min-w-[12rem]',
                width === 'content' && 'min-w-[12rem] max-w-[min(32rem,calc(100vw-1rem))]',
                width === 'responsive' && 'w-[min(var(--radix-popover-trigger-width),calc(100vw-1rem))] min-w-[12rem]',
                classNames?.content,
              )}
              >
              {searchable ? (
                <div className="relative mb-1">
                  <Search aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id={searchId}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className={cn('h-9 w-full rounded-sm border-0 bg-transparent pl-8 pr-2 text-sm outline-none ring-0 placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring', classNames?.search)}
                  />
                </div>
              ) : null}
              {clearable && selectedOptions.length > 0 ? (
                <button type="button" className="mb-1 w-full rounded-sm px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent" onClick={clearSelection}>
                  {clearLabel}
                </button>
              ) : null}
              {loading && visibleOptions.length === 0 ? (
                slots?.loading ? slots.loading() : <div role="status" className={cn('px-2 py-6 text-center text-sm text-muted-foreground', classNames?.empty)}>{loadingLabel}</div>
              ) : error && visibleOptions.length === 0 ? (
                slots?.error ? slots.error() : <div role="alert" className={cn('px-2 py-6 text-center text-sm text-destructive-text', classNames?.empty)}>{error}{onRetry ? <button type="button" className="mt-2 block w-full text-center text-foreground underline" onClick={onRetry}>{retryLabel}</button> : null}</div>
              ) : visibleOptions.length === 0 ? (
                slots?.empty ? slots.empty() : <div role="status" className={cn('px-2 py-6 text-center text-sm text-muted-foreground', classNames?.empty)}>{emptyLabel}</div>
              ) : (
                <div role={mode === 'tree' ? 'tree' : contentRole} aria-multiselectable={mode === 'multi' || undefined}>
                  {visibleOptions.map((option, index) => {
                    const selected = selectedSet.has(option.value);
                    const indicator = mode === 'radio' ? <Circle aria-hidden="true" className={cn('h-3.5 w-3.5', selected && 'fill-current')} /> : <Check aria-hidden="true" className="h-4 w-4" />;
                    return (
                      <div key={option.value}>
                        {option.group && (index === 0 || visibleOptions[index - 1]?.group !== option.group) ? (
                          <div role="presentation" className={cn('px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground', classNames?.groupLabel)}>{option.group}</div>
                        ) : null}
                        <button
                          ref={(node) => { itemRefs.current[index] = node; }}
                          type="button"
                          role={mode === 'tree' ? 'treeitem' : mode === 'radio' ? 'menuitemradio' : mode === 'checkbox' ? 'menuitemcheckbox' : isMenu ? 'menuitem' : 'option'}
                          aria-level={mode === 'tree' ? option.depth + 1 : undefined}
                          aria-selected={!isMenu && !usesCheckedMenu ? selected : undefined}
                          aria-checked={usesCheckedMenu ? selected : undefined}
                          disabled={option.disabled}
                          data-highlighted={index === activeIndex || undefined}
                          data-state={selected ? 'checked' : 'unchecked'}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => selectOption(option)}
                          className={cn(
                            'relative flex min-h-9 w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors',
                            'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                            'disabled:pointer-events-none disabled:opacity-50',
                            index === activeIndex && 'bg-accent text-accent-foreground',
                            selected && 'font-medium',
                            option.danger && 'text-destructive-text',
                            mode !== 'action' && 'pl-8',
                            classNames?.item,
                          )}
                          style={mode === 'tree' ? { paddingInlineStart: `${8 + option.depth * 16}px` } : undefined}
                        >
                          {mode !== 'action' ? <span className="absolute left-2 flex h-4 w-4 items-center justify-center">{selected ? indicator : null}</span> : null}
                          {slots?.option ? slots.option({ option, selected, highlighted: index === activeIndex }) : <>
                            {option.icon ? <span className="shrink-0">{option.icon}</span> : null}
                            <span className={cn('min-w-0 flex-1 truncate', classNames?.itemLabel)}>{option.label}</span>
                            {option.description ? <span className={cn('max-w-[45%] truncate text-xs text-muted-foreground', classNames?.itemDescription)}>{option.description}</span> : null}
                          </>}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {error && visibleOptions.length > 0 ? <div role="status" className="px-2 py-1 text-xs text-destructive-text">{error}</div> : null}
              {loading && visibleOptions.length > 0 ? <div role="status" className="px-2 py-1 text-xs text-muted-foreground">{loadingLabel}</div> : null}
              {slots?.footer ? slots.footer() : null}
              </PopoverPrimitive.Content>
            );
            return portal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content;
          })()}
        </div>
      </PopoverPrimitive.Root>
    );
  },
);
